import { useEffect, useRef } from "react";

/**
 * Auto-refresh hook.
 * - Runs `callback` every `intervalMs` while the tab is visible.
 * - Pauses when the tab is hidden (avoids wasted requests in background).
 * - Runs `callback` immediately when the user returns to the tab.
 *
 * The `enabled` flag lets consumers pause polling conditionally
 * (e.g. when the user is inspecting a historical snapshot).
 *
 * Usage:
 *   useAutoRefresh(load, 60_000);
 */
export default function useAutoRefresh(callback, intervalMs, enabled = true) {
  const cbRef = useRef(callback);
  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;

    let timer = null;
    const tick = () => {
      if (document.visibilityState === "visible") {
        try { cbRef.current?.(); } catch { /* swallow — never crash the UI */ }
      }
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(tick, intervalMs);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Immediate refresh when the tab regains focus
        try { cbRef.current?.(); } catch { /* noop */ }
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [intervalMs, enabled]);
}
